import React, { ReactNode } from 'react';
import {
  Stack,
  type StackProps,
  type SxProps,
  type Theme,
  Typography,
  type TypographyPropsVariantOverrides,
  useTheme
} from '@mui/material';
import { type Variant } from '@mui/material/styles/createTypography';
import { type OverridableStringUnion } from '@mui/types';
import { mergeSx } from 'merge-sx';

type TalentInfoFieldProps = {
  label?: string | boolean;
  fallback?: string | ReactNode;
  children?: ReactNode;
  direction?: StackProps['direction'];
  variant?: OverridableStringUnion<'inherit' | Variant, TypographyPropsVariantOverrides>;
  sx?: SxProps<Theme>;
  skipEmpty?: boolean;
};

const HeaderProperty = ({
  label,
  fallback,
  variant = 'body2',
  sx,
  skipEmpty,
  children,
  direction = 'row'
}: TalentInfoFieldProps) => {
  const { spacing } = useTheme();

  if (!label && !children && !fallback) return null;

  if (skipEmpty && !children) return null;

  return (
    <Stack
      direction={direction}
      className="HeaderProperty-root"
      sx={mergeSx(
        {
          mb: 0,
          mt: 2,
          marginInlineEnd: spacing(4),
          ':last-child': { marginInlineEnd: 0 }
        },
        sx
      )}
    >
      {label && (
        <Typography className="HeaderProperty-label" component="dt" variant={variant} color="grey.700">
          {label}
        </Typography>
      )}
      {(children || fallback) && (
        <Typography
          className="HeaderProperty-description"
          component="dd"
          variant={variant}
          sx={mergeSx(
            direction === 'row' && { marginInlineStart: spacing(1) },
            direction === 'column' && { marginTop: spacing(1) }
          )}
        >
          {children || fallback}
        </Typography>
      )}
    </Stack>
  );
};

export default HeaderProperty;
